/**
 * Defers loading the Sentry SDK (and its replay/tracing integrations) off the
 * render-critical boot path. Sentry.init() previously ran synchronously at
 * module-eval time in index.tsx, forcing ~90KB (gzip) of SDK + integrations
 * into the same network waterfall as the very first paint.
 *
 * Errors thrown before Sentry finishes loading are captured by a minimal
 * window.onerror/unhandledrejection buffer (installed synchronously, cost is
 * a couple of tiny listener registrations) and replayed into Sentry once it's
 * ready, so nothing is silently lost during the deferred window.
 */
import type * as SentryReact from '@sentry/react';

type SentryModule = typeof SentryReact;

interface BufferedError {
  kind: 'error' | 'unhandledrejection';
  error: unknown;
}

let sentryModule: SentryModule | null = null;
let sentryReadyPromise: Promise<SentryModule | null> | null = null;
const errorBuffer: BufferedError[] = [];
const MAX_BUFFERED_ERRORS = 20;

function bufferError(entry: BufferedError) {
  errorBuffer.push(entry);
  if (errorBuffer.length > MAX_BUFFERED_ERRORS) {
    errorBuffer.shift();
  }
}

function onWindowError(event: ErrorEvent) {
  bufferError({ kind: 'error', error: event.error ?? event.message });
}

function onUnhandledRejection(event: PromiseRejectionEvent) {
  bufferError({ kind: 'unhandledrejection', error: event.reason });
}

function isSentryEnabled(): boolean {
  return (
    import.meta.env.VITE_ENABLE_SENTRY === 'true' &&
    !!import.meta.env.VITE_SENTRY_DSN &&
    import.meta.env.MODE !== 'development'
  );
}

async function loadAndInitSentry(): Promise<SentryModule | null> {
  if (!isSentryEnabled()) return null;

  const Sentry = await import('@sentry/react');
  const { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } = await import('react-router-dom');
  const React = await import('react');

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        // Sentry's default finalTimeout (30s) was silently capping every
        // pageload/navigation span on /desktops/* and /spaces/* at exactly
        // 30.000s in prod — not a real app hang. Those routes keep spawning
        // background spans (websocket reconnects, run/liveness polling,
        // ~30s-interval timers throughout Space/) that reset the idle timer,
        // so the span almost never finishes via idleTimeout and instead
        // always hits the hard finalTimeout ceiling. Raising it lets the
        // real tail latency show up in Sentry instead of a flat artifact.
        finalTimeout: 60000,
      }),
      Sentry.replayIntegration({
        // Show everything in replays — only password inputs remain masked.
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
        // Note: maskAllInputs=false means no inputs are masked by default.
        // Password fields rely on browser autofill masking, not Sentry masking.
        // Capture API request/response metadata (URLs, status, duration, headers)
        // so each replay shows what network calls happened and whether they failed.
        // Bodies are not captured to avoid storing PII in Sentry.
        networkDetailAllowUrls: [window.location.origin, /\/api\//],
        networkCaptureBodies: false,
        networkRequestHeaders: ['content-type', 'x-request-id'],
        networkResponseHeaders: ['content-type', 'x-request-id'],
      }),
      // Surface console.error / console.warn calls as Sentry breadcrumbs and events.
      Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
      // Auto-capture failed fetch/XHR responses (4xx/5xx) as Sentry errors.
      Sentry.httpClientIntegration(),
    ],
    tracesSampleRate: 0.2,
    // Capture replays for every session for maximum visibility.
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    sendDefaultPii: true,
    environment: import.meta.env.MODE,
    ignoreErrors: [
      // ── Vite chunk-load / CSS preload artifacts ──────────────────────────
      // Users with a cached HTML shell from a prior deploy may reference
      // asset hashes that no longer exist on the CDN. These self-resolve
      // after the hard reload triggered by chunkRecovery (see the
      // vite:preloadError handler at the top of index.tsx).
      /Unable to preload CSS for/,
      /Importing a module script failed/,
      /Failed to fetch dynamically imported module/,
      /error loading dynamically imported module/,
      // ── Browser extension DOM mutations ─────────────────────────────────
      // Google Translate and similar extensions rewrite the DOM; React's VDOM
      // then disagrees with the real DOM. Not actionable on our end.
      /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node/,
      /Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node/,
      /NotFoundError: The object can not be found here/,
      // ── Transient device / network failures ─────────────────────────────
      // Safari emits these for camera/mic device drops, network state flips,
      // and aborted background fetches. Self-healing on next interaction.
      /NotReadableError: The I\/O read operation failed/,
      /TypeError: Load failed/,
      // ── Per-entity merge rate limit (expected back-pressure) ────────────
      // The backend rejects concurrent merges to the same entity with 429 to
      // prevent a single client (typically YJS sync) from saturating the
      // database. Caller paths defer and retry on their next save cycle, so
      // these are never user-visible and never actionable.
      /MergeDeferredError/,
      /Merge for entity .* deferred/,
    ],
  });

  // Flush anything the native buffer caught while Sentry was still loading.
  for (const buffered of errorBuffer) {
    Sentry.captureException(buffered.error);
  }
  errorBuffer.length = 0;
  window.removeEventListener('error', onWindowError);
  window.removeEventListener('unhandledrejection', onUnhandledRejection);

  sentryModule = Sentry;
  return Sentry;
}

/**
 * Installs the native error buffer immediately (cheap) and schedules the real
 * Sentry SDK load for the first idle slot after boot. Safe to call once, at
 * module-eval time.
 */
export function initSentryDeferred(): void {
  if (!isSentryEnabled()) return;
  if (sentryReadyPromise) return;

  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  sentryReadyPromise = new Promise((resolve) => {
    (window as any).requestIdleCallback(() => {
      loadAndInitSentry().then(resolve).catch(() => resolve(null));
    });
  });
}

/** Resolves once Sentry has finished loading (or immediately with null if disabled). */
export function getSentry(): Promise<SentryModule | null> {
  if (!isSentryEnabled()) return Promise.resolve(null);
  return sentryReadyPromise ?? Promise.resolve(sentryModule);
}

/** Synchronous best-effort accessor — null until getSentry()'s promise resolves. */
export function getSentrySync(): SentryModule | null {
  return sentryModule;
}
